import { useEffect, useLayoutEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export const reducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export const isTouch = () =>
  typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches;

export const WordReveal = ({ text = "", className = "" }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (reducedMotion() || !ref.current) return;
    const words = ref.current.querySelectorAll("[data-wr]");
    const tween = gsap.fromTo(
      words,
      { yPercent: 115, opacity: 0 },
      {
        yPercent: 0,
        opacity: 1,
        stagger: 0.045,
        duration: 0.55,
        ease: "power3.out",
        scrollTrigger: { trigger: ref.current, start: "top 88%" },
        onComplete: () => words.forEach((w) => w.style.setProperty("will-change", "auto")),
      }
    );
    return () => {
      tween.scrollTrigger?.kill();
      tween.kill();
    };
  }, [text]);
  const parts = String(text).split(" ");
  return (
    <span ref={ref} className={className}>
      {parts.map((w, i) => (
        <span key={i} className="inline-block overflow-hidden pb-1 align-bottom">
          <span data-wr className="inline-block will-change-transform">
            {w}
            {i < parts.length - 1 ? "\u00A0" : ""}
          </span>
        </span>
      ))}
    </span>
  );
};

export const DrawLine = ({ className = "" }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (reducedMotion() || !ref.current) return;
    const t = gsap.fromTo(
      ref.current,
      { scaleX: 0 },
      { scaleX: 1, duration: 1.1, ease: "power2.out", scrollTrigger: { trigger: ref.current, start: "top 92%" } }
    );
    return () => {
      t.scrollTrigger?.kill();
      t.kill();
    };
  }, []);
  return (
    <div ref={ref} className={`h-px origin-left bg-gradient-to-r from-crimson via-crimson/50 to-transparent ${className}`} />
  );
};

export const Tilt = ({ children, className = "", intensity = 8 }) => {
  const ref = useRef(null);
  const onMove = (e) => {
    const el = ref.current;
    if (!el || reducedMotion() || isTouch()) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    gsap.to(el, { rotateX: -py * intensity, rotateY: px * intensity, transformPerspective: 900, duration: 0.4, ease: "power1.out" });
    el.style.setProperty("--glow-x", `${(px + 0.5) * 100}%`);
    el.style.setProperty("--glow-y", `${(py + 0.5) * 100}%`);
  };
  const onLeave = () => {
    if (ref.current) gsap.to(ref.current, { rotateX: 0, rotateY: 0, duration: 0.6, ease: "power3.out" });
  };
  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={onLeave} data-cursor className={`tilt-card ${className}`} style={{ transformStyle: "preserve-3d" }}>
      {children}
    </div>
  );
};

export const Magnetic = ({ children, strength = 0.35, className = "" }) => {
  const ref = useRef(null);
  const onMove = (e) => {
    const el = ref.current;
    if (!el || reducedMotion() || isTouch()) return;
    const r = el.getBoundingClientRect();
    gsap.to(el, { x: (e.clientX - r.left - r.width / 2) * strength, y: (e.clientY - r.top - r.height / 2) * strength, duration: 0.3, ease: "power1.out" });
  };
  const onLeave = () => {
    if (ref.current) gsap.to(ref.current, { x: 0, y: 0, duration: 0.55, ease: "elastic.out(1, 0.4)" });
  };
  return (
    <span ref={ref} onMouseMove={onMove} onMouseLeave={onLeave} className={`inline-block ${className}`}>
      {children}
    </span>
  );
};

export const ScrollProgress = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    const k = reducedMotion() ? 1 : 0.12;
    let current = 0;
    let max = document.documentElement.scrollHeight - window.innerHeight;
    const onResize = () => {
      max = document.documentElement.scrollHeight - window.innerHeight;
    };
    window.addEventListener("resize", onResize, { passive: true });
    const tick = () => {
      const target = max > 0 ? window.scrollY / max : 0;
      const next = current + (target - current) * k;
      if (Math.abs(next - current) < 0.0004 && Math.abs(target - current) < 0.0004) return;
      current = next;
      el.style.transform = `translate3d(0,0,0) scaleX(${current})`;
    };
    gsap.ticker.add(tick);
    return () => {
      window.removeEventListener("resize", onResize);
      gsap.ticker.remove(tick);
    };
  }, []);
  return (
    <div
      ref={ref}
      data-testid="scroll-progress"
      className="fixed top-0 right-0 left-0 z-[95] h-[3px] origin-left bg-gradient-to-r from-blood via-crimson to-crimson shadow-[0_0_10px_rgba(211,47,47,0.7)]"
      style={{ transform: "translate3d(0,0,0) scaleX(0)" }}
    />
  );
};

// staggered hero entry for inner pages (~1.2s) - hero elements carry data-hero
export const usePageEntry = (ref, ready = true) => {
  useLayoutEffect(() => {
    if (!ready || reducedMotion() || !ref.current) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        "[data-hero]",
        { opacity: 0, y: 24 },
        { opacity: 1, y: 0, duration: 0.6, stagger: 0.16, ease: "power3.out" }
      );
    }, ref);
    return () => ctx.revert();
  }, [ready]);
};

